/**
 * Pipeline por etapas del People-flow: 5 chips (Pre / Día 1 / Semana 1 /
 * Global / Día 30) coloreados por el peor status de la etapa, con popover que
 * lista los pasos internos — extiende el patrón de OnboardingPipeline.
 */

import { currentLocale } from '@/lib/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { stepTitle } from './plain-language';
import {
  CalendarClock,
  Sunrise,
  CalendarRange,
  Globe2,
  Flag,
  type LucideIcon,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  stageSummaries,
  stageTone,
  stepLabelKey,
  type PeopleFlowStage,
  type PeopleFlowStep,
  type PeopleFlowStepStatus,
  type StageTone,
  pendingText,
} from './flow-model';

/** Colores por status (superset de STEP_CLASSES de OnboardingPipeline). */
export const FLOW_STATUS_CLASSES: Record<PeopleFlowStepStatus, string> = {
  ok: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  // Corrió, pero quedó algo a mano (sin salita / sin hueco / líder sin Slack).
  // Ámbar a propósito: en verde, People lo leía como "listo" y no lo miraba.
  ok_pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  scheduled: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400',
  waiting: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
  expired: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400',
  blocked_validation: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  na: 'bg-muted text-muted-foreground/50',
};

export const FLOW_STATUS_DOT: Record<PeopleFlowStepStatus, string> = {
  ok: 'bg-green-500',
  // Verde con anillo ámbar: el paso SALIÓ, pero dejó algo a mano (KP 21/09: el ámbar solo se leía como "en curso")
  ok_pending: 'bg-green-500 ring-2 ring-amber-400',
  scheduled: 'bg-violet-500',
  waiting: 'bg-yellow-500',
  error: 'bg-red-500',
  expired: 'bg-orange-500',
  blocked_validation: 'bg-amber-500',
  na: 'bg-muted-foreground/40',
};

/** Semáforo del chip por etapa (feedback People 31/08). */
export const STAGE_TONE_CLASSES: Record<StageTone, string> = {
  done: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  // Hecho pero con algo a mano: verde (KP 21/09: "el paso funcionó y el clapper lo recibió") con anillo ámbar y punto en la esquina
  pending_manual: 'bg-green-100 text-green-700 ring-1 ring-amber-400 dark:bg-green-900/40 dark:text-green-400 dark:ring-amber-500',
  in_progress: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
  empty: 'bg-muted text-muted-foreground/50',
};

export const STAGE_TONE_DOT: Record<StageTone, string> = {
  done: 'bg-green-500',
  // Punto de la leyenda para "hecho, queda algo a mano": verde con anillo ámbar,
  // igual que el chip (KP 21/09: el ámbar solo se leía como "en curso").
  pending_manual: 'bg-green-500 ring-2 ring-amber-400',
  in_progress: 'bg-orange-500',
  failed: 'bg-red-500',
  empty: 'bg-muted-foreground/40',
};

const STAGE_ICONS: Record<PeopleFlowStage, LucideIcon> = {
  pre_start: CalendarClock,
  day_one: Sunrise,
  week_one: CalendarRange,
  global: Globe2,
  day_thirty: Flag,
};

function formatShortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function StageChip({
  stage,
  steps,
  tone,
  done,
  total,
  validationMissing,
}: ReturnType<typeof stageSummaries>[number] & { tone: StageTone; validationMissing?: string[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const Icon = STAGE_ICONS[stage];

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <span
          className={`relative inline-flex h-7 min-w-7 cursor-default items-center justify-center gap-1 rounded-md px-1.5 ${STAGE_TONE_CLASSES[tone]}`}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <Icon className="h-4 w-4" />
          {tone === 'pending_manual' && (
            <span aria-hidden className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background" />
          )}
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={6}
        className="w-72 p-3 text-left pointer-events-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <span className="flex items-center justify-between font-semibold text-sm">
          <span className="flex items-center gap-2">
            <Icon className="h-3.5 w-3.5" />
            {t(`users.people_panel.stages.${stage}`)}
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            {done}/{total}
          </span>
        </span>
        <span className="mt-2 block space-y-1">
          {steps.map((s) => (
            <span key={String(s.stepId)} className="flex items-center gap-1.5 text-xs">
              <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${FLOW_STATUS_DOT[s.status]}`} />
              <span className="truncate">
                {String(s.stepId).startsWith('legacy_') ? t(stepLabelKey(s)) : stepTitle(t, String(s.stepId))}
              </span>
              {s.status === 'ok_pending' && s.pending && (
                <span className="ml-auto max-w-[45%] shrink-0 truncate text-amber-700 dark:text-amber-400" title={pendingText(t, s)}>
                  {pendingText(t, s)}
                </span>
              )}
              {s.status === 'scheduled' && s.dueAt && (
                <span className="ml-auto shrink-0 text-muted-foreground">{formatShortDate(s.dueAt)}</span>
              )}
              {s.status === 'error' && (
                <span className="ml-auto shrink-0 text-red-600 dark:text-red-400">
                  {String(s.stepId) === 'validation' && validationMissing && validationMissing.length > 0
                    ? t('users.people_panel.detail.missing_short', 'Faltan {{count}} datos', { count: validationMissing.length })
                    : t('users.people_panel.status.error')}
                </span>
              )}
            </span>
          ))}
        </span>
      </PopoverContent>
    </Popover>
  );
}

export function FlowStagePipeline({
  steps,
  validationMissing,
}: {
  steps: PeopleFlowStep[];
  /** Labels de campos faltantes — el popover dice cuántos en vez de "Error". */
  validationMissing?: string[];
}) {
  const summaries = stageSummaries(steps);
  return (
    <div className="flex items-center gap-1">
      {summaries.map((s) => (
        <StageChip
          key={s.stage}
          {...s}
          tone={stageTone(s.steps)}
          validationMissing={validationMissing}
        />
      ))}
    </div>
  );
}

/** Leyenda del semáforo de etapas (el detalle por paso vive en el popover). */
export function FlowLegend() {
  const { t } = useTranslation();
  // 'pending_manual' entra en la leyenda (KP 21/09: no se entendía el ámbar) y cada
  // ítem explica qué significa al pasar el mouse.
  const items: StageTone[] = ['done', 'pending_manual', 'in_progress', 'failed'];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {items.map((tone) => (
        <span key={tone} className="inline-flex cursor-help items-center gap-1.5" title={t(`users.people_panel.stage_tone_help.${tone}`)}>
          <span className={`h-2 w-2 rounded-full ${STAGE_TONE_DOT[tone]}`} />
          {t(`users.people_panel.stage_tone.${tone}`)}
        </span>
      ))}
    </div>
  );
}
